import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User as BaseUser, UserFormData, Tenant } from '../../types';
import { Role } from '../../types/rbac.types';
import { getTenants, getTenant, getSyncRegions } from '../../services/api';
import { getRoles, getRole } from '../../services/rbacApi';
import { API_URL } from '../../utils/ConfigUtils';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

// Extend User interface to include roles
interface User extends BaseUser {
  roles?: {
    id: string;
    name: string;
    application_id: string;
    is_system_role: boolean;
    description?: string;
  }[];
  tenant_level?: string;
  user_type?: 'local' | 'idp';
  provider_name?: string;
  destination?: string;
}

// Define the validation schema
const userFormSchema = z.object({
  tenant_level: z.string().min(1, 'Tenant level is required'),
  tenant_id: z.string().min(1, 'Tenant is required'),
  user_type: z.enum(['local', 'idp']),
  username: z.string().min(3, 'Username must be at least 3 characters').optional(),
  email: z.string().email('Invalid email format'),
  roles: z.string().min(1, 'Role is required'),
  status: z.enum(['active', 'inactive']).optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  provider_name: z.string().optional(),
  destination: z.string().optional(),
  // Optional list of regions to sync this user to
  sync_regions: z.array(z.string()).optional(),
});

// Define the schema type
type UserFormSchema = z.infer<typeof userFormSchema>;

interface UserFormProps {
  initialData?: User;
  onSubmit: (data: UserFormData) => void;
  isLoading?: boolean;
  disableRoleEdit?: boolean; // New prop to disable role editing
}

const UserForm: React.FC<UserFormProps> = ({
  initialData,
  onSubmit,
  isLoading = false,
  disableRoleEdit = false
}) => {
  const { user: currentUser } = useAuth();
  const [tenantLevels, setTenantLevels] = useState<string[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState<boolean>(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(false);
  const [syncRegions, setSyncRegions] = useState<string[]>([]);
  
  // Helper to normalize various ID shapes coming from API (string | {_id}|{id}|{_id:{$oid}})
  const normalizeId = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (value.id && typeof value.id === 'string') return value.id;
      if (value._id && typeof value._id === 'string') return value._id;
      if (value._id && typeof value._id.$oid === 'string') return value._id.$oid;
    }
    return '';
  };

  
  // Transform initialData to match UserFormData structure
  const getDefaultValues = useCallback((): Partial<UserFormSchema> => {
    if (initialData) {
      const normalizedTenantId = normalizeId(initialData.tenant_id);
      const firstRole = Array.isArray(initialData.roles) && initialData.roles.length > 0 ? initialData.roles[0] : undefined;
      const normalizedRoleId = normalizeId(firstRole);
      const defaults: Partial<UserFormSchema> = {
        tenant_level: initialData.tenant_level || '',
        tenant_id: normalizedTenantId || '',
        user_type: (initialData.user_type === 'idp' ? 'idp' : 'local'),
        username: initialData.username || '',
        email: initialData.email || '',
        status: (initialData.status === 'inactive' ? 'inactive' : 'active'),
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        roles: normalizedRoleId || '',
        provider_name: initialData.provider_name || '',
        destination: initialData.destination || '',
        sync_regions: initialData.sync_regions || [],
      };
      return defaults;
    }
    const emptyDefaults: Partial<UserFormSchema> = {
      status: 'active',
      roles: '',
      tenant_level: '',
      tenant_id: '',
      user_type: 'local',
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      provider_name: '',
      destination: '',
      sync_regions: [],
    };
    return emptyDefaults;
  }, [initialData]);
  
  // Initialize form with react-hook-form
  const { 
    register, 
    handleSubmit, 
    formState: { errors },
    setValue,
    watch
  } = useForm<UserFormSchema>({
    resolver: zodResolver(userFormSchema),
    defaultValues: getDefaultValues()
  });
  
  // Get the current values from the form
  const selectedTenantLevel = watch('tenant_level');
  const selectedTenantId = watch('tenant_id');
  const selectedUserType = watch('user_type');
  const selectedSyncRegions = watch('sync_regions') || [];
  
  // Filter tenants when tenant level changes - memoized to prevent unnecessary recalculations
  useEffect(() => {
    if (selectedTenantLevel && tenants.length > 0) {
      const filtered = tenants.filter(tenant => tenant.level === selectedTenantLevel);
      setFilteredTenants(filtered);
      
      // If the current tenant_id is not in the filtered list, reset it
      if (selectedTenantId && !filtered.some(t => t.id === selectedTenantId)) {
        setValue('tenant_id', '');
      }
    } else {
      setFilteredTenants([]);
    }
  }, [selectedTenantLevel, tenants, selectedTenantId, setValue]);
  
  // Load available tenants
  useEffect(() => {
    const fetchTenants = async () => {
      setIsLoadingTenants(true);
      
      try {
        let params = {};
        if (currentUser?.tenant_level === 'L0') {
        } else if (currentUser?.tenant_level === 'L1' || currentUser?.tenant_level === 'L2') {
          params = { parent_id: currentUser.tenant_id };
        } else {
          params = { id: currentUser?.tenant_id };
        }
        let tenantsData = await getTenants(params);

        // Ensure the edited user's tenant is present
        const normalizedTenantId = normalizeId(initialData?.tenant_id);
        if (normalizedTenantId) {
          const hasUserTenant = tenantsData.some(t => t.id === normalizedTenantId);
          if (!hasUserTenant) {
            try {
              const userTenant = await getTenant(normalizedTenantId);
              if (userTenant) {
                tenantsData = [...tenantsData, userTenant];
              }
            } catch (e) {
              // console.warn('[UserForm] could not fetch user tenant by id:', normalizedTenantId, e);
            }
          }
        }

        setTenants(tenantsData);
        const levelsSet = new Set(tenantsData.map(tenant => tenant.level));
        const levels = Array.from(levelsSet);
        // console.log('[UserForm] tenant levels derived:', levels);
        setTenantLevels(levels);
        if (levels.length === 1) {
          // console.log('[UserForm] only one level found, preselecting:', levels[0]);
          setValue('tenant_level', levels[0]);
        }
        if (initialData?.tenant_id && !initialData.tenant_level) {
          const userTenant = tenantsData.find(t => t.id === normalizedTenantId);
          // console.log('[UserForm] user tenant from initialData:', normalizedTenantId, userTenant);
          if (userTenant) {
            setValue('tenant_level', userTenant.level);
            // Also preselect tenant_id now that the list contains it
            setValue('tenant_id', userTenant.id as any);
            setFilteredTenants(tenantsData.filter(t => t.level === userTenant.level));
          }
        }
      } catch (error) {
        // console.error('[UserForm] Error loading tenants:', error);
      } finally {
        setIsLoadingTenants(false);
      }
    };
    fetchTenants();
  }, [currentUser, setValue, initialData]);

  // Load available sync regions from backend (from env-driven config)
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const regions = await getSyncRegions();
        setSyncRegions(regions || []);
      } catch (error) {
        // Non-blocking: it's fine if regions fail to load, user creation still works.
        // console.error('[UserForm] Error loading sync regions:', error);
      }
    };
    fetchRegions();
  }, []);
  

  
  // Load roles — basic list first (fast), enrich in background if needed
  useEffect(() => {
    let isMounted = true;

    const loadRoles = async () => {
      setIsLoadingRoles(true);

      const initialRoleId = initialData?.roles?.length
        ? normalizeId(initialData.roles[0])
        : '';

      try {
        const [basicRoles, userRole] = await Promise.all([
          getRoles(undefined, false),
          initialRoleId
            ? getRole(initialRoleId, false).catch(() => null)
            : Promise.resolve(null),
        ]);

        if (!isMounted) return;

        let mergedRoles = basicRoles;
        if (userRole && !basicRoles.some((r) => r.id === userRole.id)) {
          mergedRoles = [userRole, ...basicRoles];
        }

        setRoles(mergedRoles);

        if (initialRoleId) {
          setValue('roles', initialRoleId);
        }
      } catch {
        if (isMounted && initialRoleId) {
          try {
            const specificRole = await getRole(initialRoleId, false);
            if (specificRole && isMounted) {
              setRoles([specificRole]);
              setValue('roles', specificRole.id);
            }
          } catch {
            // ignore
          }
        }
      } finally {
        if (isMounted) {
          setIsLoadingRoles(false);
        }
      }
    };

    loadRoles();
    return () => {
      isMounted = false;
    };
  }, [initialData, setValue]);
  
  // Effect to set initial form values if initialData is provided
  useEffect(() => {
    if (initialData && tenants.length > 0 && roles.length > 0) {
      // console.log('[UserForm] setting values from initialData now that tenants & roles are loaded');
      Object.entries(initialData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'tenant_id') {
            const norm = normalizeId(value);
            // console.log('[UserForm] set tenant_id ->', norm);
            setValue('tenant_id', norm as any);
          } else if (key === 'roles') {
            const firstRole = Array.isArray(value) && value.length > 0 ? value[0] : undefined;
            const norm = firstRole ? normalizeId(firstRole) : '';
            // console.log('[UserForm] set roles ->', norm);
            if (firstRole) setValue('roles', norm as any);
          } else {
            setValue(key as any, value as any);
          }
        }
      });
      const normalizedTenantId = normalizeId(initialData.tenant_id);
      if (normalizedTenantId && !initialData.tenant_level) {
        const userTenant = tenants.find(t => t.id === normalizedTenantId);
        // console.log('[UserForm] derive tenant_level from tenant_id:', normalizedTenantId, '=>', userTenant?.level);
        if (userTenant) setValue('tenant_level', userTenant.level);
      }
    } else {
      // console.log('[UserForm] waiting to set values - tenants:', tenants.length, 'roles:', roles.length, 'has initialData:', !!initialData);
    }
  }, [initialData, setValue, tenants, roles]);

  // Effect to set tenant level when user's tenant is found
  useEffect(() => {
    const normalizedTenantId = normalizeId(initialData?.tenant_id);
    if (normalizedTenantId && tenants.length > 0) {
      const userTenant = tenants.find(t => t.id === normalizedTenantId);
      // console.log('[UserForm] tenant-level from tenants effect:', normalizedTenantId, userTenant?.level);
      if (userTenant && userTenant.level) {
        setValue('tenant_level', userTenant.level);
      }
    }
  }, [initialData, tenants, setValue]);

  // Effect to set tenant level when filtered tenants change
  useEffect(() => {
    const normalizedTenantId = normalizeId(initialData?.tenant_id);
    if (normalizedTenantId && filteredTenants.length > 0) {
      const userTenant = filteredTenants.find(t => t.id === normalizedTenantId);
      // console.log('[UserForm] tenant-level from filteredTenants effect:', normalizedTenantId, userTenant?.level);
      if (userTenant && userTenant.level) {
        setValue('tenant_level', userTenant.level);
      }
    }
  }, [initialData, filteredTenants, setValue]);

  // Effect to set roles when they are loaded
  useEffect(() => {
    // If API returned role ids list
    if (initialData?.roles && initialData.roles.length > 0) {
      const normalizedRoleId = normalizeId(initialData.roles[0]);
      if (roles.length > 0) {
        const foundRole = roles.find(r => r.id === normalizedRoleId);
        // console.log('[UserForm] set role from roles list:', normalizedRoleId, 'found:', !!foundRole);
        if (foundRole) {
          setValue('roles', foundRole.id);
          return;
        }
      }
      // If roles not yet loaded or not found, set raw id so the select reflects it once options render
      if (normalizedRoleId) {
        // console.log('[UserForm] setting raw role id before roles load:', normalizedRoleId);
        setValue('roles', normalizedRoleId as any);
        return;
      }
    }

    // Fallback: infer role by name from initialData (if no role IDs provided)
    if (roles.length > 0 && initialData) {
      const anyData: any = initialData as any;
      const candidateNames: string[] = [];

      if (typeof anyData.role === 'string' && anyData.role) candidateNames.push(anyData.role);
      if (typeof anyData.role_name === 'string' && anyData.role_name) candidateNames.push(anyData.role_name);

      // Look into applications object e.g., applications.Administration.role_name or similar
      if (anyData.applications && typeof anyData.applications === 'object') {
        try {
          for (const appKey of Object.keys(anyData.applications)) {
            const app = anyData.applications[appKey];
            if (!app) continue;
            if (typeof app.role_name === 'string') candidateNames.push(app.role_name);
            if (typeof app.role === 'string') candidateNames.push(app.role);
          }
        } catch (e) {
          // ignore
        }
      }

      // Normalize candidate names (lowercase trim)
      const norm = (s: string) => s.toLowerCase().trim();
      const uniqueNames = Array.from(new Set(candidateNames.filter(Boolean).map(norm)));
      if (uniqueNames.length > 0) {
        const foundByName = roles.find(r => uniqueNames.includes(norm(r.name)));
        // console.log('[UserForm] role inference by name - candidates:', uniqueNames, 'matched:', foundByName?.name);
        if (foundByName) {
          setValue('roles', foundByName.id);
        }
      }
    }
  }, [initialData, roles, setValue]);
  
  // Update provider name and destination when tenant changes and user type is IDP
  useEffect(() => {
    if (!selectedTenantId || selectedUserType !== 'idp') return;
    
    const selectedTenant = tenants.find(t => t.id === selectedTenantId);
    if (selectedTenant) {
      setValue('provider_name', selectedTenant.provider_name || '');
      setValue('destination', selectedTenant.destination || '');
    }
  }, [selectedTenantId, selectedUserType, tenants, setValue]);
  
  // Submit handler - memoized to prevent unnecessary recreations
  const handleFormSubmit = useCallback((data: UserFormSchema) => {
    // Use the actual selected role name instead of mapping to legacy roles
    let userRole = 'user'; // Default role if nothing is selected
    if (data.roles) {
      const selectedRole = roles.find(role => role.id === data.roles);
      
      if (selectedRole) {
        // Use the actual role name directly
        userRole = selectedRole.name;
      } else {
        // If role not found in loaded roles, try to fetch it directly
        try {
          // Make a synchronous request to get the role name
          const xhr = new XMLHttpRequest();
          xhr.open('GET', `${API_URL}/roles/${data.roles}`, false);  // false makes it synchronous
          
          // Add authorization header
          const token = localStorage.getItem('token');
          if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          
          xhr.send();
          
          if (xhr.status === 200) {
            const roleData = JSON.parse(xhr.responseText);
            if (roleData && roleData.name) {
              userRole = roleData.name;
            } else {
              userRole = 'custom_role';
            }
          } else {
            userRole = 'custom_role';
          }
        } catch (error) {
          // console.error('Error fetching role directly:', error);
          userRole = 'custom_role';
        }
      }
    }
    
    // Convert the form data to UserFormData using type assertion
    const userData = {
      ...data,
      role: userRole, // Use the actual role name from RBAC
      roles: data.roles ? [data.roles] : [], // Ensure roles is always an array
      status: data.status || 'active',
      // For local users, ensure username and password are provided
      // For IDP users, ensure provider_name and destination are provided
      username: data.user_type === 'local' ? data.username : undefined,
      provider_name: data.user_type === 'idp' ? data.provider_name : undefined,
      destination: data.user_type === 'idp' ? data.destination : undefined
    } as UserFormData;
    
    // Remove debug logging for production build
            // Final user data prepared
    
    // Perform validation based on user type
    if (data.user_type === 'local' && !data.username) {
      alert('Username is required for local users');
      return;
    }
    // Password is auto-generated and emailed by backend for new local users; no client-side requirement.
    
    onSubmit(userData);
  }, [roles, onSubmit]);
  
  // Memoize the tenant level options to prevent unnecessary re-renders
  const tenantLevelOptions = useMemo(() => {
    return tenantLevels.map((level) => (
      <option key={level} value={level}>
        {level}
      </option>
    ));
  }, [tenantLevels]);
  
  // Memoize the tenant options to prevent unnecessary re-renders
  const tenantOptions = useMemo(() => {
    return filteredTenants.map((tenant) => (
      <option key={tenant.id} value={tenant.id}>
        {tenant.name}
      </option>
    ));
  }, [filteredTenants]);
  

  
  // Memoize the role options to prevent unnecessary re-renders
  const roleOptions = useMemo(() => {
    return roles.map((role) => (
      <option key={role.id} value={role.id}>
        {role.name}
        {role.is_system_role && ' (System)'}
      </option>
    ));
  }, [roles]);
  
  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Tenant Selection */}
      <div className="space-y-4 pt-4">
        <h3 className="text-xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">Tenant Selection</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Tenant Level */}
          <div className="form-group mb-4">
            <label htmlFor="tenant_level" className="block text-sm font-medium text-gray-300 mb-1.5">
              Tenant Level <span className="text-blue-400">*</span>
              {isLoadingTenants && (
                <span className="ml-2 inline-block w-4 h-4 border-2 border-t-transparent border-blue-500 rounded-full animate-spin"></span>
              )}
            </label>
            <select
              id="tenant_level"
              className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200 appearance-none disabled:opacity-70 disabled:cursor-not-allowed"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: `right 0.5rem center`,
                backgroundSize: `1.5em 1.5em`,
                backgroundRepeat: `no-repeat`
              }}
              disabled={isLoadingTenants || tenantLevels.length === 0}
              {...register('tenant_level')}
            >
              <option value="">Select Tenant Level</option>
              {tenantLevelOptions}
            </select>
            {errors.tenant_level && <p className="text-red-400 text-xs mt-1">{errors.tenant_level.message}</p>}
          </div>
          
          {/* Tenant */}
          <div className="form-group mb-4">
            <label htmlFor="tenant_id" className="block text-sm font-medium text-gray-300 mb-1.5">
              Tenant <span className="text-blue-400">*</span>
              {isLoadingTenants && (
                <span className="ml-2 inline-block w-4 h-4 border-2 border-t-transparent border-blue-500 rounded-full animate-spin"></span>
              )}
            </label>
            <select
              id="tenant_id"
              className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200 appearance-none disabled:opacity-70 disabled:cursor-not-allowed"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: `right 0.5rem center`,
                backgroundSize: `1.5em 1.5em`,
                backgroundRepeat: `no-repeat`
              }}
              disabled={isLoadingTenants || !selectedTenantLevel || filteredTenants.length === 0}
              {...register('tenant_id')}
            >
              <option value="">Select Tenant</option>
              {tenantOptions}
            </select>
            {errors.tenant_id && <p className="text-red-400 text-xs mt-1">{errors.tenant_id.message}</p>}
          </div>
        </div>
      </div>
      
      {/* User Type Selection */}
      <div className="space-y-4 pt-4 border-t border-indigo-800/20">
        <h3 className="text-xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">User Type</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-group mb-4">
            <label htmlFor="user_type" className="block text-sm font-medium text-gray-300 mb-1.5">
              Authentication Type <span className="text-blue-400">*</span>
            </label>
            <select
              id="user_type"
              className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200 appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: `right 0.5rem center`,
                backgroundSize: `1.5em 1.5em`,
                backgroundRepeat: `no-repeat`
              }}
              {...register('user_type')}
            >
              <option value="local">Local</option>
              <option value="idp">IDP</option>
            </select>
          </div>
        </div>
      </div>

      {/* Region Sync (optional) */}
      <div className="space-y-3 pt-4 border-t border-indigo-800/20">
        <h3 className="text-xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">
          Region Sync
        </h3>
        {syncRegions.length === 0 ? (
          <p className="text-sm text-gray-400">
            No sync regions are configured in the environment. Users will remain local to this region.
          </p>
        ) : (
          <>
            <p className="text-xs text-gray-400">
              Select which regions this user should be synchronized to. Sync uses the same cross-region engine as product sync.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {syncRegions.map((region) => (
                <label
                  key={region}
                  className="inline-flex items-center gap-2 text-sm text-gray-200 bg-gray-900/40 border border-indigo-800/30 rounded-lg px-3 py-2 hover:border-indigo-500/60 transition-colors"
                >
                  <input
                    type="checkbox"
                    value={region}
                    className="h-4 w-4 rounded border-gray-500 bg-gray-900 text-indigo-500 focus:ring-indigo-500"
                    {...register('sync_regions')}
                    defaultChecked={selectedSyncRegions?.includes(region)}
                  />
                  <span className="uppercase tracking-wide text-xs text-gray-200">{region}</span>
                </label>
              ))}
            </div>
          </>
        )}
      </div>
      
      {/* User Information */}
      <div className="space-y-4 pt-4 border-t border-indigo-800/20">
        <h3 className="text-xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">User Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Email */}
          <div className="form-group mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
              Email <span className="text-blue-400">*</span>
            </label>
            <input
              id="email"
              type="email"
              className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200"
              placeholder="Enter email address"
              {...register('email')}
            />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
          </div>
          
          {/* First Name */}
          <div className="form-group mb-4">
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-300 mb-1.5">First Name</label>
            <input
              id="first_name"
              type="text"
              className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200"
              placeholder="Enter first name"
              {...register('first_name')}
            />
            {errors.first_name && <p className="text-red-400 text-xs mt-1">{errors.first_name.message}</p>}
          </div>
          
          {/* Last Name */}
          <div className="form-group mb-4">
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-300 mb-1.5">Last Name</label>
            <input
              id="last_name"
              type="text"
              className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200"
              placeholder="Enter last name"
              {...register('last_name')}
            />
            {errors.last_name && <p className="text-red-400 text-xs mt-1">{errors.last_name.message}</p>}
          </div>
          
          {/* Local Authentication Fields */}
          {selectedUserType === 'local' && (
            <>
              {/* Username */}
              <div className="form-group mb-4">
                <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1.5">
                  Username <span className="text-blue-400">*</span>
                </label>
                <input
                  id="username"
                  type="text"
                  className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200"
                  placeholder="Enter username"
                  {...register('username')}
                />
                {errors.username && <p className="text-red-400 text-xs mt-1">{errors.username.message}</p>}
              </div>
              {/* We email a temporary password on create. For edits, use Reset button. */}
              {!initialData && (
                <p className="text-xs text-gray-400 -mt-2 mb-2">A temporary password will be generated and emailed to the user.</p>
              )}
            </>
          )}
          
          {/* IDP Authentication Fields */}
          {selectedUserType === 'idp' && (
            <>
              {/* Provider Name */}
              <div className="form-group mb-4">
                <label htmlFor="provider_name" className="block text-sm font-medium text-gray-300 mb-1.5">
                  Provider Name
                </label>
                <input
                  id="provider_name"
                  type="text"
                  className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200"
                  placeholder="Provider name"
                  {...register('provider_name')}
                />
              </div>
              
              {/* Destination */}
              <div className="form-group mb-4">
                <label htmlFor="destination" className="block text-sm font-medium text-gray-300 mb-1.5">
                  Destination
                </label>
                <input
                  id="destination"
                  type="text"
                  className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200"
                  placeholder="Destination"
                  {...register('destination')}
                />
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Application and Roles */}
      <div className="space-y-4 pt-4 border-t border-indigo-800/20">
        <h3 className="text-xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">Roles</h3>
        
        {/* Roles */}
        <div className="form-group">
          <label htmlFor="roles" className="block text-sm font-medium text-gray-300 mb-1.5">
            Roles <span className="text-blue-400">*</span>
            {isLoadingRoles && (
              <span className="ml-2 inline-block w-4 h-4 border-2 border-t-transparent border-blue-500 rounded-full animate-spin"></span>
            )}
            {disableRoleEdit && (
              <span className="ml-2 text-xs text-yellow-400">(Cannot change your own role)</span>
            )}
          </label>
          
          {roles.length === 0 && isLoadingRoles ? (
            <div className="flex items-center justify-center p-4 bg-gray-900/60 border border-indigo-800/20 rounded-lg">
              <Loader2 className="animate-spin h-5 w-5 text-blue-500 mr-2" />
              <span className="text-sm text-gray-400">Loading roles...</span>
            </div>
          ) : roles.length === 0 ? (
            <div className="text-sm text-gray-400 p-3 bg-gray-900/60 border border-indigo-800/20 rounded-lg">
              No roles available
            </div>
          ) : (
            <select
              id="roles"
              className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200 appearance-none disabled:opacity-70 disabled:cursor-not-allowed"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: `right 0.5rem center`,
                backgroundSize: `1.5em 1.5em`,
                backgroundRepeat: `no-repeat`
              }}
              disabled={disableRoleEdit}
              {...register('roles')}
            >
              <option value="">Select Role</option>
              {roleOptions}
            </select>
          )}
          {errors.roles && <p className="text-red-400 text-xs mt-1">{errors.roles.message}</p>}
        </div>
      </div>
      
      {/* Status */}
      <div className="space-y-4 pt-4 border-t border-indigo-800/20">
        <h3 className="text-xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">Status</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Status */}
          <div className="form-group mb-4">
            <label htmlFor="status" className="block text-sm font-medium text-gray-300 mb-1.5">Status</label>
            <select
              id="status"
              className="w-full bg-gray-900/60 border border-indigo-800/20 text-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 focus:outline-none transition-all duration-200 appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: `right 0.5rem center`,
                backgroundSize: `1.5em 1.5em`,
                backgroundRepeat: `no-repeat`
              }}
              {...register('status')}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {errors.status && <p className="text-red-400 text-xs mt-1">{errors.status.message}</p>}
          </div>
        </div>
      </div>
      
      {/* Form Actions */}
      <div className="flex justify-end space-x-2 pt-4 border-t border-indigo-800/20 mt-6">
        <button 
          type="submit"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-70"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 border-t-transparent border-white animate-spin"></div>
              <span>Saving...</span>
            </>
          ) : (
            <span>{initialData ? 'Update' : 'Create'}</span>
          )}
        </button>
      </div>
    </form>
  );
};

export default UserForm; 